import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Clock, AlertCircle, Copy, ExternalLink } from "lucide-react";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  // A job checkout's success URL carries job=1 (#86). The status answer says kind "job" too, but a 404 carries nothing.
  const isJobCheckout = searchParams.get("job") === "1";

  const [status, setStatus] = useState("checking"); // checking | success | pending | failed
  const [zipCode, setZipCode] = useState("");
  const [zipCodes, setZipCodes] = useState([]);
  const [kind, setKind] = useState("");
  const [jobId, setJobId] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  const pollStatus = async (attempt = 0) => {
    if (!sessionId) { setStatus("failed"); return; }
    const isCouponSession = sessionId.startsWith("coupon_");
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      const { payment_status, zip_code, zip_codes, kind: answerKind, job_id } = res.data;
      if (answerKind) setKind(answerKind);
      if (job_id) setJobId(job_id);
      if (zip_codes && zip_codes.length > 0) setZipCodes(zip_codes);
      if (zip_code) setZipCode(zip_code);
      if (payment_status === "paid") {
        setStatus("success");
      } else if (isCouponSession) {
        // Sec 8: Coupon sessions are immediately active — never fall into pending/failed
        // Retry up to 3 times quickly in case of transient DB read lag
        if (attempt < 3) {
          setAttempts(attempt + 1);
          setTimeout(() => pollStatus(attempt + 1), 800);
        } else {
          setStatus("success"); // Treat as success — coupon claims are pre-activated
        }
      } else if (attempt < 6) {
        setAttempts(attempt + 1);
        setTimeout(() => pollStatus(attempt + 1), 2500);
      } else {
        setStatus("pending");
      }
    } catch (err) {
      const is404 = err?.response?.status === 404;
      // For coupon sessions, a 404 means the transaction doesn't exist — show failed
      if (isCouponSession && is404) {
        setStatus("failed");
      } else if (attempt < 3) {
        setTimeout(() => pollStatus(attempt + 1), isCouponSession ? 800 : 3000);
      } else {
        setStatus(isCouponSession ? "success" : "failed");
      }
    }
  };

  useEffect(() => {
    pollStatus(0);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isJob = kind === "job" || isJobCheckout;

  const badgeHtml = `<a href="https://nextdayrefund.com" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none;">
  <div style="background:#003366;color:#fff;border-radius:8px;padding:12px 18px;display:inline-flex;align-items:center;gap:10px;font-family:Arial,sans-serif;box-shadow:0 2px 8px rgba(0,0,0,0.15);">
    <span style="background:#2ECC71;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;flex-shrink:0;">&#10003;</span>
    <div>
      <div style="font-size:10px;text-transform:uppercase;letter-spacing:1px;opacity:0.7;margin-bottom:2px;">Listed on</div>
      <div style="font-weight:bold;font-size:13px;">NextDayRefund.com</div>
    </div>
  </div>
</a>`;

  const copyBadge = () => {
    navigator.clipboard.writeText(badgeHtml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="section-container py-16 max-w-2xl">

        {/* Checking */}
        {status === "checking" && (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center" data-testid="payment-checking">
            <div className="animate-spin w-12 h-12 border-2 border-[#003366] border-t-transparent rounded-full mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Verifying Your Payment</h2>
            <p className="text-[#64748B]">Please wait while we confirm your payment with Stripe...</p>
            {attempts > 0 && <p className="text-xs text-[#64748B] mt-2">Attempt {attempts}/6...</p>}
          </div>
        )}

        {/* Success: job post (#86). No ZIP line, no badge (#32), no end date until #93 is decided. */}
        {status === "success" && isJob && (
          <div data-testid="job-payment-success">
            <div className="bg-white border-2 border-[#2ECC71] rounded-xl p-8 text-center mb-6">
              <div className="w-16 h-16 bg-[#2ECC71] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-black text-[#0F172A] mb-2">Payment Successful!</h1>
              <div className="text-lg font-bold text-[#003366] mb-2">Your job post is live.</div>
              <p className="text-[#64748B]">It is now listed on the NextDayRefund Jobs board. You can find it any time under My Jobs in your portal.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              {jobId && (
                <Link to={`/jobs/${jobId}`} className="btn-primary flex-1 text-center" data-testid="view-job-btn">
                  View Your Job Post
                </Link>
              )}
              <Link to="/portal" className="btn-outline flex-1 text-center" data-testid="job-portal-btn">
                Go to My Portal
              </Link>
            </div>
          </div>
        )}

        {/* Success */}
        {status === "success" && !isJob && (
          <div data-testid="payment-success">
            <div className="bg-white border-2 border-[#2ECC71] rounded-xl p-8 text-center mb-6">
              <div className="w-16 h-16 bg-[#2ECC71] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-white" />
              </div>
              <h1 className="text-2xl font-black text-[#0F172A] mb-2">Payment Successful!</h1>
              {zipCodes.length > 1 ? (
                <div className="text-lg font-bold text-[#003366] mb-2">
                  {zipCodes.length} ZIP Codes secured —{" "}
                  {zipCodes.map((z, i) => (
                    <span key={z}><span className="text-[#2ECC71]">{z}</span>{i < zipCodes.length - 1 ? ", " : ""}</span>
                  ))}
                </div>
              ) : zipCode ? (
                <div className="text-lg font-bold text-[#003366] mb-2">
                  ZIP Code <span className="text-[#2ECC71]">{zipCode}</span> is now exclusively yours!
                </div>
              ) : null}
              <p className="text-[#64748B]">Your exclusive territory is active for the next 12 months. Leads will be routed directly to your business.</p>
            </div>

            {/* Badge Code */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="badge-section">
              <h3 className="font-bold text-[#0F172A] mb-2">Your Badge</h3>
              <p className="text-[#64748B] text-sm mb-4">Copy and paste this HTML code onto your website to display your badge:</p>

              {/* Badge Preview */}
              <div className="mb-4 flex justify-center">
                <div style={{background:"#003366",color:"#fff",borderRadius:"8px",padding:"12px 18px",display:"inline-flex",alignItems:"center",gap:"10px",fontFamily:"Arial,sans-serif"}}>
                  <span style={{background:"#2ECC71",color:"#fff",borderRadius:"50%",width:"24px",height:"24px",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:"bold",fontSize:"14px",flexShrink:0}}>✓</span>
                  <div>
                    <div style={{fontSize:"10px",textTransform:"uppercase",letterSpacing:"1px",opacity:0.7,marginBottom:"2px"}}>Listed on</div>
                    <div style={{fontWeight:"bold",fontSize:"13px"}}>NextDayRefund.com</div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <pre className="bg-[#F8FAFC] border border-slate-200 rounded-lg p-4 text-xs text-[#334155] overflow-x-auto whitespace-pre-wrap" data-testid="badge-code">
                  {badgeHtml}
                </pre>
                <button
                  onClick={copyBadge}
                  className="absolute top-3 right-3 flex items-center gap-1.5 bg-white border border-slate-200 rounded px-3 py-1.5 text-xs font-semibold text-[#003366] hover:bg-[#003366] hover:text-white transition-all"
                  data-testid="copy-badge-btn"
                >
                  {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy Code</>}
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/portal" className="btn-primary flex-1 text-center" data-testid="go-to-portal-btn">
                Go to My Portal
              </Link>
              <Link to="/claim" className="btn-outline flex-1 text-center" data-testid="claim-another-btn">
                Claim Another ZIP
              </Link>
            </div>
          </div>
        )}

        {/* Pending */}
        {status === "pending" && (
          <div className="bg-white border border-[#FFD700]/40 bg-[#FFF8E1] rounded-xl p-8 text-center" data-testid="payment-pending">
            <Clock size={48} className="text-[#FFD700] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Payment Processing</h2>
            <p className="text-[#64748B] mb-4">Your payment is still being processed. This can take a moment.</p>
            <p className="text-sm text-[#64748B] mb-6">
              {isJob
                ? "If you've completed payment, your job post will go live shortly. Check My Jobs in your portal for updates."
                : "If you've completed payment, your ZIP code will be activated shortly. Check your portal for updates."}
            </p>
            <Link to="/portal" className="btn-primary inline-block" data-testid="pending-portal-btn">Check My Portal</Link>
          </div>
        )}

        {/* Failed */}
        {status === "failed" && (
          <div className="bg-white border border-red-200 rounded-xl p-8 text-center" data-testid="payment-failed">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Payment Not Found</h2>
            {isJob ? (
              <>
                <p className="text-[#64748B] mb-4">We couldn't verify your payment. If you completed checkout, check My Jobs in your portal before trying again, or contact support.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/portal" className="btn-primary inline-block" data-testid="failed-portal-btn">Check My Portal</Link>
                  <Link to="/post-a-job" className="btn-outline inline-block" data-testid="failed-retry-btn">Try Again</Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#64748B] mb-4">We couldn't verify your payment. If you completed checkout, please contact support.</p>
                <Link to="/claim" className="btn-primary inline-block" data-testid="failed-retry-btn">Try Again</Link>
              </>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
